import { useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { RefreshCcw, Download, AlertCircle, FileText } from 'lucide-react';
import { useAdminStatistics, StatsPeriod } from '@/hooks/useAdminStatistics';
import { exportToCSV } from '@/utils/exportStatistics';
import { exportStatisticsToPDF } from '@/utils/exportStatisticsPDF';
import { StatsOverviewCards } from './StatsOverviewCards';
import { UsersGrowthChart } from './charts/UsersGrowthChart';
import { ActivityChart } from './charts/ActivityChart';
import { PlansDistributionChart } from './charts/PlansDistributionChart';
import { PlansAdoptionChart } from './charts/PlansAdoptionChart';
import { ResourcesUsageChart } from './charts/ResourcesUsageChart';
import { OnlineUsersChart } from './charts/OnlineUsersChart';
import { ChartSkeleton } from './charts/ChartSkeleton';
import { useToast } from '@/hooks/use-toast';

export const StatisticsTab = () => {
  const { period, setPeriod, data, isLoading, error, refetch } = useAdminStatistics();
  const { toast } = useToast();

  // Refs for chart cards
  const usersGrowthRef = useRef<HTMLDivElement>(null);
  const activityRef = useRef<HTMLDivElement>(null);
  const plansDistributionRef = useRef<HTMLDivElement>(null);
  const plansAdoptionRef = useRef<HTMLDivElement>(null);
  const resourcesUsageRef = useRef<HTMLDivElement>(null);
  const onlineUsersRef = useRef<HTMLDivElement>(null);

  const handleExportPDF = async () => {
    if (!data) return;

    try {
      toast({
        title: 'Gerando PDF...',
        description: 'Por favor, aguarde enquanto preparamos o relatório.',
      });

      await exportStatisticsToPDF(data, period, {
        usersGrowth: usersGrowthRef.current,
        activity: activityRef.current,
        plansDistribution: plansDistributionRef.current,
        plansAdoption: plansAdoptionRef.current,
        resourcesUsage: resourcesUsageRef.current,
        onlineUsers: onlineUsersRef.current,
      });

      toast({
        title: 'PDF exportado!',
        description: 'O relatório foi baixado com sucesso.',
      });
    } catch (error) {
      console.error('Erro ao exportar PDF:', error);
      toast({
        title: 'Erro ao exportar',
        description: 'Não foi possível gerar o PDF. Tente novamente.',
        variant: 'destructive',
      });
    }
  };

  const periods: Array<{ value: StatsPeriod; label: string }> = [
    { value: '24h', label: '24 horas' },
    { value: '7d', label: '7 dias' },
    { value: '15d', label: '15 dias' },
    { value: '30d', label: '30 dias' },
    { value: '60d', label: '60 dias' },
    { value: '90d', label: '90 dias' },
  ];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-64 mt-2" />
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Skeleton key={i} className="h-9 w-20" />
              ))}
            </div>
          </CardContent>
        </Card>

        <div className="grid gap-4 md:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-5 w-32" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-10 w-24" />
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-4 w-48 mt-2" />
              </CardHeader>
              <CardContent>
                <ChartSkeleton />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle className="text-destructive flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              Erro ao Carregar Estatísticas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              {error?.message || 'Não foi possível carregar os dados estatísticos.'}
            </p>
            <Button onClick={() => refetch()} variant="outline" className="w-full">
              <RefreshCcw className="h-4 w-4 mr-2" />
              Tentar Novamente
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Nenhum dado disponível</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Period Selector */}
      <Card>
        <CardHeader>
          <CardTitle>Período de Análise</CardTitle>
          <CardDescription>Selecione o período para visualizar as estatísticas</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {periods.map((p) => (
              <Button
                key={p.value}
                variant={period === p.value ? 'default' : 'outline'}
                onClick={() => setPeriod(p.value)}
                size="sm"
                className="whitespace-nowrap"
              >
                {p.label}
              </Button>
            ))}
            <Button variant="ghost" onClick={() => refetch()} size="sm">
              <RefreshCcw className="h-4 w-4" />
            </Button>
            <Button variant="outline" onClick={() => exportToCSV(data, period)} size="sm">
              <Download className="h-4 w-4 mr-2" />
              Exportar CSV
            </Button>
            <Button variant="outline" onClick={handleExportPDF} size="sm">
              <FileText className="h-4 w-4 mr-2" />
              Exportar PDF
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Overview Cards */}
      <StatsOverviewCards data={data} />

      {/* Charts Grid */}
      <div className="grid gap-6 grid-cols-1 lg:grid-cols-2">
        <Card ref={usersGrowthRef}>
          <CardHeader>
            <CardTitle>Crescimento de Usuários</CardTitle>
            <CardDescription>Novos cadastros no período</CardDescription>
          </CardHeader>
          <CardContent>
            <UsersGrowthChart data={data.users} />
          </CardContent>
        </Card>

        <Card ref={activityRef}>
          <CardHeader>
            <CardTitle>Atividade da Plataforma</CardTitle>
            <CardDescription>Conversas, documentos e casos criados</CardDescription>
          </CardHeader>
          <CardContent>
            <ActivityChart
              conversations={data.conversations}
              documents={data.documents}
              casos={data.casos}
            />
          </CardContent>
        </Card>

        <Card ref={plansDistributionRef}>
          <CardHeader>
            <CardTitle>Distribuição de Planos</CardTitle>
            <CardDescription>Usuários por tipo de plano</CardDescription>
          </CardHeader>
          <CardContent>
            <PlansDistributionChart data={data.plans} />
          </CardContent>
        </Card>

        <Card ref={plansAdoptionRef}>
          <CardHeader>
            <CardTitle>Adesão de Planos</CardTitle>
            <CardDescription>Novas assinaturas no período</CardDescription>
          </CardHeader>
          <CardContent>
            <PlansAdoptionChart data={data.plansAdoption} />
          </CardContent>
        </Card>

        <Card ref={resourcesUsageRef}>
          <CardHeader>
            <CardTitle>Consumo de Recursos</CardTitle>
            <CardDescription>Tokens utilizados (input/output)</CardDescription>
          </CardHeader>
          <CardContent>
            <ResourcesUsageChart data={data.tokens} />
          </CardContent>
        </Card>

        <Card ref={onlineUsersRef}>
          <CardHeader>
            <CardTitle>Usuários Ativos</CardTitle>
            <CardDescription>Atividade ao longo do período</CardDescription>
          </CardHeader>
          <CardContent>
            <OnlineUsersChart data={data.online} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
